// -----------------------------------------------------------------------------
// TreatmentContextHeader
//
// Pure display header for the COMPLETED-TREATMENT wrap-up hub. Reads everything
// from the `row` passed by the lead table — NO API calls, NO Redux.
//
// Row shape note (investigated): the TREATMENT_STARTED grid rows are produced by
// parseV2ToV1 (src/utils/fieldUtils.js), which spreads the lead fields to the
// top level and nests the treatment under `row.lead_treatments[0]` (with
// `treatment_data` = the v2 treatment object, `treatment` = its name). The raw
// V2-flat shape (row.lead.first_name, row.treatment, row.price …) only exists
// pre-parse. To be backward-compatible with BOTH shapes, every field is read
// from the nested treatment first, then falls back to the flat field.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Stack } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { getTransitionDate } from 'src/utils/dataGridUtils';

// Read a treatment-scoped field from either the parsed (nested) or raw (flat) row.
function pickTreatmentField(row, key) {
    const tx = row?.lead_treatments?.[0];
    if (tx && tx[key] != null) return tx[key];
    return row?.[key];
}

// Resolve the treatment NAME robustly across the shapes we actually see.
// On the V2-flat row `treatment` is an OBJECT (treatment.treatment_item.name),
// while the parsed/nested shape exposes treatment_data.treatment_item.name and
// some legacy rows carry treatment as a plain string. Take the first non-empty.
function resolveTreatmentName(row) {
    const tx = row?.lead_treatments?.[0];
    // `useLeadTableHelper` row shape (the actual one reaching this hub): the name
    // lives in `treatment_name`, and per-treatment data under `treatmentData[0]`.
    const txd = row?.treatmentData?.[0];
    const candidates = [
        // useLeadTableHelper row (confirmed runtime shape)
        typeof row?.treatment_name === 'string' ? row.treatment_name : '',
        txd?.treatment_data?.treatment_item?.name,
        typeof txd?.treatment === 'string' ? txd.treatment : '',
        // nested parsed shape
        tx?.treatment_data?.treatment_item?.name,
        // flat object shape
        row?.treatment?.treatment_item?.name,
        // flat string shape (fallback)
        typeof row?.treatment === 'string' ? row.treatment : '',
        // legacy/edge fallbacks
        tx?.treatment_data?.name,
        typeof tx?.treatment === 'string' ? tx.treatment : '',
    ];
    const name = candidates.find((c) => typeof c === 'string' && c.trim() !== '');
    return name || '';
}

function formatStartedDate(stateTransitionLogs) {
    const started = getTransitionDate(stateTransitionLogs);
    if (!started) return null;
    // getTransitionDate returns either a moment (multi-log case) or a
    // 'DD-MM-YYYY HH:mm:ss' string (single-log case).
    const m = moment.isMoment(started) ? started : moment(started, 'DD-MM-YYYY HH:mm:ss');
    if (!m.isValid()) return null;
    return m;
}

function StatPill({ icon, label, value, tone }) {
    const palette = {
        neutral: { bg: 'rgba(0,0,0,0.04)', color: '#1d1d1f', iconColor: '#6e6e73' },
        positive: { bg: 'rgba(52,199,89,0.10)', color: '#1d1d1f', iconColor: '#34c759' },
        warning: { bg: 'rgba(255,149,0,0.10)', color: '#1d1d1f', iconColor: '#ff9500' },
    };
    const p = palette[tone] || palette.neutral;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.75,
                bgcolor: p.bg,
                borderRadius: '10px',
                minWidth: 0,
            }}
        >
            <Iconify icon={icon} width={16} height={16} sx={{ color: p.iconColor, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 500, color: '#6e6e73', letterSpacing: '-0.005em', lineHeight: 1.2 }}>
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: p.color,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}
StatPill.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    tone: PropTypes.oneOf(['neutral', 'positive', 'warning']),
};
StatPill.defaultProps = { tone: 'neutral' };

export default function TreatmentContextHeader({ row }) {
    // Patient name — parsed rows spread lead fields to top level; raw rows nest
    // under row.lead. Support both.
    const firstName = row?.lead?.first_name ?? row?.first_name ?? '';
    const lastName = row?.lead?.last_name ?? row?.last_name ?? '';
    const patientName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Patient';

    const treatmentName = resolveTreatmentName(row) || '—';
    const price = pickTreatmentField(row, 'price');
    const isFeePending = pickTreatmentField(row, 'is_treatment_fee_pending');
    const payments = pickTreatmentField(row, 'lead_treatment_payments') || [];
    const paymentMethod = Array.isArray(payments) ? payments?.[0]?.payment_type : null;
    const hasClinCheck = !!pickTreatmentField(row, 'leadtreatment_clincheck');
    const appointments = pickTreatmentField(row, 'appointments') || [];
    const appointmentCount = Array.isArray(appointments) ? appointments.length : 0;

    const startedMoment = formatStartedDate(pickTreatmentField(row, 'state_transition_logs'));
    const startedLabel = startedMoment ? startedMoment.format('D MMM YYYY') : null;
    const durationLabel = startedMoment ? startedMoment.fromNow(true) : null;

    const priceLabel = price != null && price !== '' ? `£${price}` : '—';

    return (
        <Box
            sx={{
                p: 3,
                bgcolor: '#ffffff',
                borderRadius: '14px',
                width: '100%',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
        >
            {/* In-progress frame: a started treatment is an ongoing phase, not a
                "wrap up". The amber pulse icon + "· In progress" chip read as
                ongoing rather than complete. */}
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        bgcolor: 'rgba(255,171,0,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="solar:heart-pulse-bold-duotone" width={22} height={22} sx={{ color: '#FFAB00' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', lineHeight: 1.2 }}>
                            {patientName}&apos;s treatment
                        </Typography>
                        <Box
                            component="span"
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: '980px',
                                bgcolor: 'rgba(255,171,0,0.14)',
                                color: '#B07A00',
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.01em',
                                textTransform: 'uppercase',
                                lineHeight: 1.4,
                            }}
                        >
                            In progress
                        </Box>
                    </Stack>
                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mt: 0.25 }}>
                        {treatmentName}
                        {startedLabel ? ` · started ${startedLabel}${durationLabel ? ` (${durationLabel})` : ''}` : ''}
                    </Typography>
                </Box>
            </Stack>

            {/* Context stat pills */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                    gap: 1,
                }}
            >
                <StatPill icon="bxs:pound" label="Treatment price" value={priceLabel} />
                <StatPill
                    icon={isFeePending ? 'eva:alert-circle-fill' : 'eva:checkmark-circle-2-fill'}
                    label="Treatment fee"
                    value={isFeePending ? 'Pending' : 'Paid'}
                    tone={isFeePending ? 'warning' : 'positive'}
                />
                <StatPill
                    icon="eva:credit-card-fill"
                    label="Payment method"
                    value={paymentMethod || '—'}
                />
                <StatPill
                    icon="eva:calendar-fill"
                    label="Appointments"
                    value={appointmentCount}
                />
                <StatPill
                    icon={hasClinCheck ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-outline'}
                    label="ClinCheck"
                    value={hasClinCheck ? 'Present' : 'None'}
                    tone={hasClinCheck ? 'positive' : 'neutral'}
                />
                {startedLabel && (
                    <StatPill icon="eva:clock-fill" label="Started" value={startedLabel} />
                )}
            </Box>
        </Box>
    );
}

TreatmentContextHeader.propTypes = {
    row: PropTypes.object.isRequired,
};
